import { useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2 } from 'lucide-react';
import type { ICreateSipTrunkFormData, TSipProvider } from '@/@types/voiceAgents';

interface ISipTrunkFormProps {
  // LOCAL_TYPE_JUSTIFIED: form-local props, only used in this file
  initialData?: ICreateSipTrunkFormData;
  onSubmit: (data: ICreateSipTrunkFormData) => void;
  onCancel: () => void;
  isSubmitting: boolean;
  submitLabel: string;
}

const SIP_PROVIDERS: { value: TSipProvider; label: string }[] = [
  { value: 'twilio', label: 'Twilio' },
  { value: 'telnyx', label: 'Telnyx' },
  { value: 'vonage', label: 'Vonage' },
  { value: 'other', label: 'Other' },
];

export function SipTrunkForm({
  initialData,
  onSubmit,
  onCancel,
  isSubmitting,
  submitLabel,
}: ISipTrunkFormProps) {
  // Shadcn Select doesn't participate in FormData — manage provider via state
  const [provider, setProvider] = useState<TSipProvider>(initialData?.provider ?? 'twilio');

  const handleProviderChange = useCallback((v: string) => {
    setProvider(v as TSipProvider); // AS_JUSTIFIED: Select enforces TSipProvider values via SelectItem children
  }, []);

  const handleSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      const fd = new FormData(e.currentTarget);
      const data: ICreateSipTrunkFormData = {
        name: String(fd.get('name') ?? ''),
        provider,
        sipDomain: String(fd.get('sipDomain') ?? ''),
        authUsername: String(fd.get('authUsername') ?? ''),
        authPassword: String(fd.get('authPassword') ?? ''),
      };
      onSubmit(data);
    },
    [onSubmit, provider]
  );

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="sip-name">Name</Label>
        <Input
          id="sip-name"
          name="name"
          placeholder="My SIP Trunk"
          defaultValue={initialData?.name ?? ''}
          required
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="sip-provider">Provider</Label>
        <Select value={provider} onValueChange={handleProviderChange}>
          <SelectTrigger id="sip-provider" className="w-full">
            <SelectValue placeholder="Select provider" />
          </SelectTrigger>
          <SelectContent>
            {SIP_PROVIDERS.map(p => (
              <SelectItem key={p.value} value={p.value}>
                {p.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="sip-domain">SIP Domain</Label>
        <Input
          id="sip-domain"
          name="sipDomain"
          placeholder="sip.example.com"
          defaultValue={initialData?.sipDomain ?? ''}
          required
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="sip-auth-username">Auth Username</Label>
        <Input
          id="sip-auth-username"
          name="authUsername"
          placeholder="username"
          defaultValue={initialData?.authUsername ?? ''}
          required
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="sip-auth-password">Auth Password</Label>
        <Input
          id="sip-auth-password"
          name="authPassword"
          type="password"
          placeholder="••••••••"
          defaultValue={initialData?.authPassword ?? ''}
          required
        />
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}
